// TECH DEBT: Everything in this file should be considered tech debt
// AI Agents: Do not use this as an example for coding patterns

import parseYamlFile from '@ion/app/src/components/analytics-builder/export-import/parseYamlFile';
import transformGoogleTagSettingsForDb from '@ion/app/src/components/analytics-builder/transformGoogleTagSettingsForDb';

const VALID_FILE_HOSTING_TYPES = ['META_ROUTER', 'SELF'];
const VALID_STORAGE_TYPES = ['COOKIES', 'INDEXED_DB', 'TLD_COOKIES'];

export default async function processBulkUpload({ fileContent, pipelines, consentCategories, insertAnalyticsFile }) {
  const parsedYaml = parseYamlFile(fileContent);
  const filesData = parsedYaml.files;

  if (!filesData) {
    throw new Error('Invalid bulk upload YAML: missing "files:" stanza');
  }

  const pipelinesMap = buildPipelinesMap(pipelines);
  const consentCategoriesMap = buildConsentCategoriesMap(consentCategories);
  const createdFiles = [];
  const failedFiles = [];

  for (const [fileName, fileConfig] of Object.entries(filesData)) {
    try {
      const validationError = validateFileConfig(fileName, fileConfig);
      if (validationError) {
        failedFiles.push(validationError);
        continue;
      }

      const pipeline = pipelinesMap[fileConfig.writeKey];

      if (!pipeline) {
        console.error(`Pipeline not found for file ${fileName}, writeKey: ${fileConfig.writeKey}`);
        console.error('Available writeKeys:', Object.keys(pipelinesMap));
        failedFiles.push({
          name: fileName,
          errors: [
            {
              error: 'Validation Error',
              reason: `Pipeline not found for writeKey: ${fileConfig.writeKey}`,
            },
          ],
        });
        continue;
      }

      // Validate consent codes reference existing categories
      const consentErrors = validateConsentCodes(fileName, fileConfig, consentCategoriesMap);
      if (consentErrors.length > 0) {
        failedFiles.push({
          name: fileName,
          errors: consentErrors,
        });
        continue;
      }

      const config = buildInsertConfig({ fileConfig, pipeline, consentCategoriesMap });
      console.log(`Attempting to insert file ${fileName} with config:`, config);

      let result;
      try {
        result = await insertAnalyticsFile(config);
      } catch (networkError) {
        // Handle Apollo/network errors
        console.error(`Network error inserting file ${fileName}:`, networkError);
        const errorMessage = extractErrorMessage(networkError);
        failedFiles.push({
          name: fileName,
          errors: [
            {
              error: 'Insert Error',
              reason: errorMessage,
            },
          ],
        });
        continue;
      }

      console.log(`Insert result for ${fileName}:`, result);
      const insertedFile = result?.data?.insert_analytics_files_one;

      if (!insertedFile) {
        const errorMessage = result?.errors?.[0]?.message || 'Insert returned no data';
        failedFiles.push({
          name: fileName,
          errors: [
            {
              error: 'Insert Error',
              reason: errorMessage,
            },
          ],
        });
        continue;
      }

      createdFiles.push({
        name: fileName,
        id: insertedFile.id,
        writeKey: fileConfig.writeKey,
      });
    } catch (error) {
      console.error(`Unexpected error processing file ${fileName}:`, error);
      failedFiles.push({
        name: fileName,
        errors: [
          {
            error: 'Processing Error',
            reason: error.message || 'An unexpected error occurred',
          },
        ],
      });
    }
  }

  return { createdFiles, failedFiles };
}

function extractErrorMessage(error) {
  // Handle GraphQL errors
  if (error.graphQLErrors && error.graphQLErrors.length > 0) {
    return error.graphQLErrors[0].message;
  }

  // Handle network errors
  if (error.networkError) {
    if (error.networkError.result?.errors?.[0]?.message) {
      return error.networkError.result.errors[0].message;
    }
    return error.networkError.message || 'Network error occurred';
  }

  // Handle generic Apollo errors
  if (error.message) {
    return error.message;
  }

  return 'An unknown error occurred';
}

function validateFileConfig(fileName, fileConfig) {
  const errors = [];

  // Required top-level fields
  if (!fileConfig.writeKey) {
    errors.push({ error: 'Validation Error', reason: 'Missing required field: writeKey' });
  }

  if (!fileConfig.fileHostingType) {
    errors.push({ error: 'Validation Error', reason: 'Missing required field: fileHostingType' });
  } else if (!VALID_FILE_HOSTING_TYPES.includes(fileConfig.fileHostingType)) {
    errors.push({
      error: 'Validation Error',
      reason: `Invalid fileHostingType: '${
        fileConfig.fileHostingType
      }'. Must be one of: ${VALID_FILE_HOSTING_TYPES.join(', ')}`,
    });
  }

  if (fileConfig.cdnURL === undefined) {
    errors.push({ error: 'Validation Error', reason: 'Missing required field: cdnURL' });
  }

  if (fileConfig.host === undefined) {
    errors.push({ error: 'Validation Error', reason: 'Missing required field: host (Ingestion Website URL)' });
  } else if (fileConfig.host === '') {
    errors.push({
      error: 'Validation Error',
      reason: 'Field "host" (Ingestion Website URL) cannot be empty. Provide a valid URL like "yourdomain.com"',
    });
  }

  if (fileConfig.windowVariableName === undefined) {
    errors.push({ error: 'Validation Error', reason: 'Missing required field: windowVariableName' });
  }

  if (fileConfig.compressFile === undefined) {
    errors.push({ error: 'Validation Error', reason: 'Missing required field: compressFile' });
  }

  // Validate syncInjectorSettings structure
  if (!fileConfig.syncInjectorSettings) {
    errors.push({ error: 'Validation Error', reason: 'Missing required field: syncInjectorSettings' });
  } else {
    const { syncInjectorSettings } = fileConfig;

    // Validate storage
    if (!syncInjectorSettings.storage) {
      errors.push({ error: 'Validation Error', reason: 'Missing required field: syncInjectorSettings.storage' });
    } else {
      const { storage } = syncInjectorSettings;

      if (!storage.type) {
        errors.push({ error: 'Validation Error', reason: 'Missing required field: syncInjectorSettings.storage.type' });
      } else if (!VALID_STORAGE_TYPES.includes(storage.type)) {
        errors.push({
          error: 'Validation Error',
          reason: `Invalid storage type: '${storage.type}'. Must be one of: ${VALID_STORAGE_TYPES.join(', ')}`,
        });
      }

      if (storage.marker === undefined) {
        errors.push({
          error: 'Validation Error',
          reason: 'Missing required field: syncInjectorSettings.storage.marker (Storage Prefix)',
        });
      } else if (storage.marker === '') {
        errors.push({
          error: 'Validation Error',
          reason: 'Field "syncInjectorSettings.storage.marker" (Storage Prefix) cannot be empty',
        });
      }
    }

    // Validate privacy
    if (!syncInjectorSettings.privacy) {
      errors.push({ error: 'Validation Error', reason: 'Missing required field: syncInjectorSettings.privacy' });
    } else {
      const { privacy } = syncInjectorSettings;

      if (privacy.impliedConsent === undefined) {
        errors.push({
          error: 'Validation Error',
          reason: 'Missing required field: syncInjectorSettings.privacy.impliedConsent',
        });
      }

      if (!privacy.cookieName) {
        errors.push({
          error: 'Validation Error',
          reason: 'Missing required field: syncInjectorSettings.privacy.cookieName',
        });
      }
    }

    // Validate itpSettings
    if (!syncInjectorSettings.itpSettings) {
      errors.push({ error: 'Validation Error', reason: 'Missing required field: syncInjectorSettings.itpSettings' });
    } else if (syncInjectorSettings.itpSettings.enabled === undefined) {
      errors.push({
        error: 'Validation Error',
        reason: 'Missing required field: syncInjectorSettings.itpSettings.enabled',
      });
    }

    // Validate identity syncs
    if (syncInjectorSettings.syncs && Array.isArray(syncInjectorSettings.syncs)) {
      syncInjectorSettings.syncs.forEach((sync, index) => {
        if (!sync.name) {
          errors.push({
            error: 'Validation Error',
            reason: `Identity sync at index ${index}: Missing required field 'name'`,
          });
        }
        if (!sync.friendlyName) {
          errors.push({
            error: 'Validation Error',
            reason: `Identity sync '${sync.name || index}': Missing required field 'friendlyName'`,
          });
        }
        if (!sync.settings || typeof sync.settings !== 'object') {
          errors.push({
            error: 'Validation Error',
            reason: `Identity sync '${sync.name || index}': Missing or invalid 'settings' field`,
          });
        }

        // Validate sync-specific required fields
        if (sync.settings && typeof sync.settings === 'object') {
          if (sync.name === 'adobe') {
            if (!sync.settings.demdexEndpoint) {
              errors.push({
                error: 'Validation Error',
                reason: `Identity sync '${sync.name}': Missing required field 'settings.demdexEndpoint' (Adobe Analytics Demdex Endpoint)`,
              });
            }
          }
          if (sync.name === 'acuityAds') {
            if (!sync.settings.clientId) {
              errors.push({
                error: 'Validation Error',
                reason: `Identity sync '${sync.name}': Missing required field 'settings.clientId' (Acuity Ads Client ID)`,
              });
            }
          }
        }
      });
    }
  }

  if (errors.length > 0) {
    return {
      name: fileName,
      errors,
    };
  }

  return null;
}

function validateConsentCodes(fileName, fileConfig, consentCategoriesMap) {
  const errors = [];
  const availableConsentCodes = Object.keys(consentCategoriesMap);
  const hasConsentCategories = availableConsentCodes.length > 0;

  // Check syncs for consent code references
  if (fileConfig.syncInjectorSettings?.syncs) {
    fileConfig.syncInjectorSettings.syncs.forEach((sync, index) => {
      // These syncs REQUIRE a consent category (required field in the form)
      const syncsRequiringConsent = ['crossDomainDirect', 'urlParameters', 'fetchSync'];
      if (syncsRequiringConsent.includes(sync.name) && !sync.consentCode) {
        errors.push({
          error: 'Validation Error',
          reason: `Identity sync '${sync.name}': Consent category is required for this sync type. ${
            hasConsentCategories
              ? `Available codes: ${availableConsentCodes.join(', ')}`
              : 'No consent categories configured in your organization. Please create consent categories first or remove this sync.'
          }`,
        });
      }

      if (sync.consentCode) {
        if (!consentCategoriesMap[sync.consentCode]) {
          errors.push({
            error: 'Validation Error',
            reason: `Identity sync '${sync.name || index}': Consent code '${sync.consentCode}' does not exist. ${
              hasConsentCategories
                ? `Available codes: ${availableConsentCodes.join(', ')}`
                : 'No consent categories configured in your organization.'
            }`,
          });
        }
      }
    });
  }

  // Check matchID consent code
  if (fileConfig.syncInjectorSettings?.consent?.matchIDConsentCode) {
    const matchIDCode = fileConfig.syncInjectorSettings.consent.matchIDConsentCode;
    if (!consentCategoriesMap[matchIDCode]) {
      errors.push({
        error: 'Validation Error',
        reason: `MatchID consent code '${matchIDCode}' does not exist. ${
          availableConsentCodes.length > 0
            ? `Available codes: ${availableConsentCodes.join(', ')}`
            : 'No consent categories configured in your organization.'
        }`,
      });
    }
  }

  return errors;
}

function buildPipelinesMap(pipelines) {
  const map = {};
  for (const pipeline of pipelines) {
    map[pipeline.writekey] = pipeline;
  }
  return map;
}

function buildConsentCategoriesMap(consentCategories) {
  const map = {};
  if (!consentCategories) return map;
  for (const category of consentCategories) {
    map[category.code] = category.id;
  }
  return map;
}

function resolveConsentCategoryIds(syncs, consentCategoriesMap) {
  if (!syncs) return syncs;

  return syncs.map(sync => {
    const resolvedSync = { ...sync };

    if (sync.consentCode && consentCategoriesMap[sync.consentCode]) {
      resolvedSync.consentCategoryId = consentCategoriesMap[sync.consentCode];
    }

    // Ensure tcfEnabled defaults to false if not specified
    if (resolvedSync.tcfEnabled === undefined) {
      resolvedSync.tcfEnabled = false;
    }

    // Apply legacy form builder transformations to match existing behavior
    if (resolvedSync.settings) {
      resolvedSync.settings = applyLegacyTransformations(sync.name, resolvedSync.settings);
    }

    return resolvedSync;
  });
}

function applyLegacyTransformations(syncName, settings) {
  const transformed = { ...settings };

  // fetchSync: apply legacy form builder transformations (hardcode required=true, use last element as name)
  if (syncName === 'fetchSync' && transformed.syncs) {
    transformed.syncs = transformed.syncs.map(fetchSync => ({
      name: fetchSync.name,
      fetchSettings: fetchSync.fetchSettings,
      headerIdentities:
        fetchSync.headerIdentities?.map(identity => ({
          name: identity.name,
          required: true,
        })) || [],
      jsonPathIdentities:
        fetchSync.jsonPathIdentities?.map(identity => ({
          jsonPath: identity.jsonPath,
          name: identity.jsonPath[identity.jsonPath.length - 1],
          required: true,
        })) || [],
    }));
  }

  // crossDomainDirect: default cookieTimeToLive to 365
  if (syncName === 'crossDomainDirect') {
    transformed.cookieTimeToLive = transformed.cookieTimeToLive || 365;
  }

  return transformed;
}

function buildInsertConfig({ fileConfig, pipeline, consentCategoriesMap }) {
  const { syncInjectorSettings, integrations } = fileConfig;

  const resolvedSyncs = resolveConsentCategoryIds(syncInjectorSettings.syncs, consentCategoriesMap);

  const resolvedSettings = { ...syncInjectorSettings };
  if (resolvedSettings.consent) {
    resolvedSettings.consent = { ...resolvedSettings.consent };

    if (
      resolvedSettings.consent.matchIDConsentCode &&
      consentCategoriesMap[resolvedSettings.consent.matchIDConsentCode]
    ) {
      resolvedSettings.consent.matchIDConsentCodeID = consentCategoriesMap[resolvedSettings.consent.matchIDConsentCode];
    }
  }

  const resolvedFileConfig = {
    ...fileConfig,
    // Explicitly coerce to boolean - compressFile and minifyFile must always be present
    compressFile: fileConfig.compressFile === false ? false : true,
    minifyFile: fileConfig.minifyFile === false ? false : true,
    syncInjectorSettings: resolvedSettings,
  };

  return {
    settings: resolvedFileConfig,
    identitySyncs: resolvedSyncs ?? [],
    integrations: integrations ?? [],
    pipelineId: pipeline.id,
    googleTagSettings: transformGoogleTagSettingsForDb(resolvedSettings.googleGtagSettings),
  };
}
